import React, { useState, useEffect, useRef } from "react";
import HandlePromoWrapper from "./HandlePromoWrapper";
import { authFetch } from "./utils/api";
import "./Packages.css";

const Packages = ({ quoteId, showLinePrices, documentType, isOpen }) => {
  const [packagesData, setPackagesData] = useState({
    1: { quote: null, itemQuotes: [] },
  });
  const [activeTab, setActiveTab] = useState(1);
  const [promotions, setPromotions] = useState([]);
  const [financing, setFinancing] = useState([]);
  const [isScrolled, setIsScrolled] = useState(false);
  const containerRef = useRef(null); // Safe ref for scroll detection

  useEffect(() => {
    const load = async () => {
      const [p, f] = await Promise.all([
        authFetch('/promotions/'),
        authFetch('/financing/')
      ]);
      setPromotions(p.filter(x => x.visible));
      setFinancing(f.filter(x => x.active));
    };
    load();
  }, []);

  // Scroll detection for sticky shadow
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      setIsScrolled(container.scrollTop > 0);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Create a new package by cloning base data
  const addPackage = async () => {
    const base = packagesData[1];
  
    if (!base.quote) {
      alert("Base data is still loading. Please wait.");
      return;
    }
  
    const newId = Object.keys(packagesData).length + 1;
    const cloned = {
      quote: structuredClone(base.quote),
      itemQuotes: structuredClone(base.itemQuotes),
      promotions: structuredClone(promotions),
      financing: structuredClone(financing),
      crm_promotions: base.quote?.crm_promotions || []
    };
  
    setPackagesData((prev) => ({ ...prev, [newId]: cloned }));
    setActiveTab(newId);
  };

  // Alternative addPackage with retry loop (kept as-is)
  const addPackage1 = async () => {
    const base = packagesData[1];

    if (!base.quote) {
      console.log("Waiting for base data...");
      let tries = 0;

      while (!packagesData[1]?.quote && tries < 10) {
        await new Promise((r) => setTimeout(r, 500));
        tries++;
        console.log("Checking base data...", tries);
      }

      if (!packagesData[1]?.quote) {
        alert("Base data not loaded yet. Try again.");
        return;
      }
    }

    const newId = Object.keys(packagesData).length + 1;
    const baseReady = packagesData[1];

    const cloned = {
      quote: structuredClone(baseReady.quote),
      itemQuotes: structuredClone(baseReady.itemQuotes),
    };

    setPackagesData((prev) => ({ ...prev, [newId]: cloned }));
    setActiveTab(newId);
  };

  // Delete a package (cannot delete base)
  const deletePackage = (id) => {
    if (id === 1) return;
    const newData = { ...packagesData };
    delete newData[id];
    setPackagesData(newData);
    setActiveTab(1);
  };

  // Called when a package saves or updates
  const handlePackageSave = (id, updatedData) => {
    setPackagesData((prev) => ({
      ...prev,
      [id]: { ...prev[id], ...updatedData },
    }));
  };

  // Called by base wrapper when data loads from API
  const handleBaseDataLoaded = (quote, itemQuotes) => {
    setPackagesData((prev) => ({
      ...prev,
      1: { 
        quote, 
        itemQuotes, 
        promotions: structuredClone(promotions),
        financing: structuredClone(financing) 
      }
    }));
  };

  const tabs = Object.keys(packagesData);

  return (
    <div
      ref={containerRef}
      className={`packages-container ${isScrolled ? 'scrolled' : ''}`}
      style={{ maxHeight: '80vh', overflowY: 'auto' }} // Adjust as needed
    >
      <div className="packages-tabs">
        <div className="packages-tab-buttons">
          {tabs.map((id) => (
            <div
              key={id}
              className={`packages-tab-item ${
                activeTab === Number(id) ? "active" : ""
              }`}
            >
              <button
                className="packages-tab"
                onClick={() => setActiveTab(Number(id))}
              >
                Package {id} {id === "1" ? "(Base)" : ""}
              </button>

              {id !== "1" && (
                <button
                  className="packages-close-btn"
                  onClick={() => deletePackage(Number(id))}
                  title="Close tab"
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>

        <button
          onClick={addPackage} // Uses the enhanced version
          className="packages-add-button"
          disabled={!packagesData[1]?.quote}
        >
          + Add Configuration
        </button>
      </div>

      <div className="packages-tab-content">
        {tabs.map((id) => {
          const tabId = Number(id);
          const isActive = activeTab === tabId;

          return (
            <div
              key={id}
              className={`tab-panel ${isActive ? 'active-pdf-capture' : ''}`}  // ← THIS ONE LINE
              style={{
                opacity: isActive ? 1 : 0,
                pointerEvents: isActive ? 'auto' : 'none',
                transition: 'opacity 0.15s ease',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
              }}
            >
              <HandlePromoWrapper
                tabId={tabId}
                quoteId={quoteId}
                showLinePrices={showLinePrices}
                documentType={documentType}
                isOpen={isOpen}
                packageData={packagesData[id]}
                onSavePackage={(updated) => handlePackageSave(tabId, updated)}
                onBaseDataLoaded={handleBaseDataLoaded}
                crmPromotions={packagesData[id]?.crm_promotions}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Packages;